import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";

const roleLabel = { admin: "Amministratore", member: "Membro", viewer: "Sola lettura" };

export default function InviteRegister() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ loading: true, valid: false, role: null });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => { setState({ loading: false, valid: true, role: data.role }); if (data.email) setEmail(data.email); })
      .catch(() => setState({ loading: false, valid: false, role: null }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-invite", { token, email, name, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-primary-foreground" /></div>
          <span className="font-heading font-black tracking-tight">ITASSESS</span>
        </div>
        {!state.valid ? (
          <div className="border border-destructive/30 bg-destructive/5 p-6">
            <h2 className="font-heading font-bold text-lg tracking-tight">Invito non valido</h2>
            <p className="text-sm text-muted-foreground mt-2">Questo invito è scaduto o è già stato utilizzato.</p>
            <Button variant="outline" className="rounded-none mt-4" onClick={() => navigate("/login")}>Vai al login</Button>
          </div>
        ) : (
          <>
            <h2 className="font-heading font-black text-2xl tracking-tight">Accetta invito</h2>
            <p className="text-sm text-muted-foreground mt-1">Sei stato invitato come <span className="font-medium text-foreground">{roleLabel[state.role]}</span>. Crea il tuo account.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div><Label className="data-label">Nome</Label><Input data-testid="invite-name-input" className="mt-1 rounded-none" value={name} onChange={(e) => setName(e.target.value)} required /></div>
              <div><Label className="data-label">Email</Label><Input type="email" data-testid="invite-email-input" className="mt-1 rounded-none" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
              <div><Label className="data-label">Password</Label><Input type="password" data-testid="invite-password-input" className="mt-1 rounded-none" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
              {error && <div data-testid="invite-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2">{error}</div>}
              <Button type="submit" data-testid="invite-submit-btn" disabled={submitting} className="w-full rounded-none">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea account
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
